import { GameConstants } from "../constants";

/*
    What adrenaline does, worked out in one place for the server's player (`Player.update`) and the healing items'
    descriptions on the HUD (0.97, a tester's, passed on by the owner on 23 October 2026: "fix these tester complaints
    please": "When hovering cola it says it restores 10 adrenaline, however its not exactly clear what adrenaline is, as
    other consumables specify restoring health ... It may be clearer if it just simply says 'restores 10 health over
    time'"). Cola, Field Meat, Tablets and the Vaccine Syringe give it. While a player has some it heals them, 1 health a
    second with a little left, up to 2.75 with a full bar, and speeds them up, up to 15%; it drains half a point a second.
    So a Cola taken with none heals 24 health over 20 seconds: its description says that (`adrenalineEffect`).
*/

/** adrenaline lost a millisecond (half a point a second), times the player's `adrenDrain` */
export const ADRENALINE_DRAIN_PER_MS = 0.0005;

/**
 * The healing a second with x adrenaline, modelled around these three points:
 *
 *  adren. | healing (hp/s)
 *  -------|---------------
 *     0   |       1
 *    30   |     1.875
 *    100  |      2.75
 *
 * With a logarithmic regression model: y = b•log[c](x + d) + a, or, using the change of base law,
 * y = b•log(x + d) / log(c) + a (https://www.desmos.com/calculator/idwbtpnzbv)
 */
const HEAL = { a: -2.2153107223876285, b: -1.9660534157593246, c: 0.14899999980029943, d: 22.5 } as const;

/**
 * The speed multiplier with x adrenaline, modelled around these three points:
 *
 *  adren. | speed multiplier
 *  -------|---------------
 *     0   |       1
 *    30   |     1.10
 *    100  |     1.15
 *
 * With a logarithmic regression model: y = b•log[c](x + d) + a, or, using the change of base law,
 * y = b•log(x + d) / log(c) + a (https://www.desmos.com/calculator/sgimzzda0b)
 */
const SPEED = { a: 0.944297822457, b: -0.0158132859327, c: 0.699999999995, d: 3.51269916486 } as const;

/** the health healed a second with this much adrenaline (`HEAL`) */
export function adrenalineHealRate(adrenaline: number): number {
    const { a, b, c, d } = HEAL;
    return (b * Math.log(adrenaline + d)) / Math.log(c) + a;
}

/** the speed multiplier with this much adrenaline (`SPEED`) */
export function adrenalineSpeed(adrenaline: number): number {
    const { a, b, c, d } = SPEED;
    return (b * Math.log(adrenaline + d)) / Math.log(c) + a;
}

export interface AdrenalineEffect {
    /** how long it lasts (s) */
    readonly seconds: number
    /** the health it heals meanwhile (hurt enough to take it all) */
    readonly health: number
    /** the most it speeds a player up (a multiplier: 1.06 is 6% faster), as it's taken */
    readonly topSpeed: number
}

/**
 * What `amount` adrenaline does to a player who had none: how long it lasts, the health it heals meanwhile and how much
 * faster they run as it's taken (with nothing else changing how fast it drains). More taken on top of some lasts as
 * long again and heals a little more, the rate being higher.
 */
export function adrenalineEffect(amount: number, maxAdrenaline: number = GameConstants.player.maxAdrenaline): AdrenalineEffect {
    const start = Math.max(0, Math.min(amount, maxAdrenaline));
    const perSecond = ADRENALINE_DRAIN_PER_MS * 1000;
    // the healing over the time it drains: ∫ rate(x) dx from 0 to `start`, over the drain a second (rate(x) is
    // k•ln(x + d) + a, k = b / ln(c): its integral k•((x + d)•ln(x + d) − (x + d)) + a•x)
    const { a, b, c, d } = HEAL;
    const k = b / Math.log(c);
    const integral = (x: number): number => k * ((x + d) * Math.log(x + d) - (x + d)) + a * x;
    return {
        seconds: start / perSecond,
        health: (integral(start) - integral(0)) / perSecond,
        topSpeed: start > 0 ? adrenalineSpeed(start) : 1
    };
}
